/**
 * Preserves which site page (games hub or topic) the user browsed from so game detail
 * routes can keep chip highlighting and listing rows in sync.
 *
 * Internal navigation uses clean `/games/[slug]` links (no tracking params) and carries the
 * origin in-session via `sessionStorage`. A deliberate single `from` query is only added
 * to shared links (Share button). Recipients recover that origin in the browser from `from=`.
 */

import type { SitePageSection } from "@/lib/site-pages-server";

/**
 * Query key appended ONLY to deliberately shared `/games/[gameSlug]` links (Share button).
 * Never placed on internal `<Link>`s, so it adds no crawl surface.
 *
 * Value is the topic slug (e.g. `from=3rd-grade`) or the reserved sentinel `from=games` for the
 * games hub. Topic slugs are unique, so a single param is enough to recover the origin.
 */
export const PAGE_ORIGIN_FROM_QUERY = "from";

/** Reserved `from` value identifying the games hub (vs a topic slug). */
export const PAGE_ORIGIN_GAMES_VALUE = "games";

/** sessionStorage key holding the in-session came-from origin. */
export const PAGE_ORIGIN_STORAGE_KEY = "playmath:page-origin";

/** How long a stored origin stays valid (ms). Guards stale origins on later direct visits. */
const PAGE_ORIGIN_TTL_MS = 30 * 60 * 1000;

export interface PageOriginContext {
  section: SitePageSection;
  /** Topic slug when section is "topics"; null for games hub */
  slug: string | null;
}

interface StoredPageOrigin extends PageOriginContext {
  ts: number;
}

function firstParam(value: string | string[] | undefined): string | undefined {
  if (value === undefined) return undefined;
  return Array.isArray(value) ? value[0] : value;
}

function appendPageOriginQuery(
  qs: URLSearchParams,
  origin: PageOriginContext | null | undefined
): void {
  if (!origin) return;

  if (origin.section === "games") {
    qs.set(PAGE_ORIGIN_FROM_QUERY, PAGE_ORIGIN_GAMES_VALUE);
    return;
  }

  const slug = origin.slug?.trim();
  if (!slug) return;

  qs.set(PAGE_ORIGIN_FROM_QUERY, slug);
}

/**
 * Parse origin from Next.js app route `searchParams` (the deliberate single `from` share key).
 */
export function parsePageOriginFromSearchParams(
  searchParams: Record<string, string | string[] | undefined> | undefined
): PageOriginContext | null {
  if (!searchParams) return null;
  const fromRaw = firstParam(searchParams[PAGE_ORIGIN_FROM_QUERY])?.trim().toLowerCase() ?? "";
  if (!fromRaw) return null;
  if (fromRaw === PAGE_ORIGIN_GAMES_VALUE) {
    return { section: "games", slug: null };
  }
  return { section: "topics", slug: fromRaw };
}

/**
 * Parse origin from a browser query string (`?from=fractions` or `from=fractions`).
 * Public game routes stay cacheable by reading `from=` on the client, not the server.
 */
export function parsePageOriginFromQueryString(search: string): PageOriginContext | null {
  const qs = search.startsWith("?") ? search.slice(1) : search;
  if (!qs) return null;
  return parsePageOriginFromSearchParams(Object.fromEntries(new URLSearchParams(qs)));
}

/**
 * Build a clean `/games/:gameSlug` for internal navigation. Origin is NOT encoded in the URL.
 */
export function buildGameHref(
  gameSlug: string,
  extraParams?: Record<string, string | undefined>
): string {
  const path = `/games/${gameSlug}`;
  if (!extraParams) return path;
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(extraParams)) {
    if (v !== undefined && v !== "") qs.set(k, v);
  }
  const q = qs.toString();
  return q ? `${path}?${q}` : path;
}

/**
 * Append the share origin params to an existing absolute/relative URL string.
 * Returns the input unchanged when there is no origin to encode.
 */
export function appendPageOriginToUrl(
  url: string,
  origin: PageOriginContext | null | undefined
): string {
  if (!origin) return url;
  const probe = new URLSearchParams();
  appendPageOriginQuery(probe, origin);
  if ([...probe.keys()].length === 0) return url;

  const [base, hash] = url.split("#");
  const separator = base.includes("?") ? "&" : "?";
  const withParams = `${base}${separator}${probe.toString()}`;
  return hash ? `${withParams}#${hash}` : withParams;
}

/** Back navigation target for a direct-visit game page from preserved origin context. */
export function resolveGameReturnHref(origin: PageOriginContext | null | undefined): string {
  if (!origin) return "/games";
  if (origin.section === "games") return "/games";
  const slug = origin.slug?.trim();
  if (!slug) return "/games";
  return `/games/${slug}`;
}

/**
 * Persist the came-from origin for the current session. No-op outside the browser.
 */
export function writePageOrigin(origin: PageOriginContext | null | undefined): void {
  if (typeof window === "undefined") return;
  try {
    if (!origin) {
      window.sessionStorage.removeItem(PAGE_ORIGIN_STORAGE_KEY);
      return;
    }
    const payload: StoredPageOrigin = { ...origin, ts: Date.now() };
    window.sessionStorage.setItem(PAGE_ORIGIN_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Storage can throw (private mode, quota). Origin context is best-effort.
  }
}

/**
 * Read the came-from origin for the current session, or null when absent/stale/unavailable.
 */
export function readPageOrigin(): PageOriginContext | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(PAGE_ORIGIN_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredPageOrigin> | null;
    if (!parsed || (parsed.section !== "games" && parsed.section !== "topics")) {
      return null;
    }
    if (typeof parsed.ts === "number" && Date.now() - parsed.ts > PAGE_ORIGIN_TTL_MS) {
      return null;
    }
    if (parsed.section === "topics") {
      const slug = parsed.slug?.trim();
      if (!slug) return null;
      return { section: "topics", slug };
    }
    return { section: "games", slug: null };
  } catch {
    return null;
  }
}
